import { useQueries } from "@tanstack/react-query";
import { customerRolesApi } from "../helpers/dck_helper";
import { roleCanApprove } from "../helpers/approvalMeta";

/**
 * 「哪些角色能當審核者」（票 38）。
 *
 * 判定規則只有一條，寫在 `helpers/approvalMeta.roleCanApprove`：
 * **`Approval` 模組是 `Edit`**。這一頁 hook 只負責把 modules 拿到手。
 *
 * ## 為什麼不能只看 GetAll
 *
 * 8/16 實測：`DcCustomerRole/GetAll` 的每一列 `modules` 都是空陣列，
 * modules 只有 `GetById` 才給（票 28）。直接拿列表判就會是「所有角色都不能審核」
 * —— 而且是**安靜地**全空，跟「真的沒人有權限」長得一模一樣。
 *
 * 所以：列表已經帶 modules（mock、以及後端哪天補上時）就直接用，沒帶才補打
 * `GetById`。而且只打**候選人身上真的出現過的 role_id**（testenv 上是 1～2 個），
 * 不是整份角色表 —— 這一頁只是個送審彈窗，不該為它掃九支請求。
 *
 * 回傳：
 *   canApprove(roleId) → boolean
 *   isLoading / error  → 補打的那批 GetById 的狀態（沒有要補打時恆為 false/null）
 */

const ROLE_DETAIL_STALE_TIME = 5 * 60 * 1000;

export function useApproverRoles(roles, roleIds) {
  // 去重後保留原始值（numeric id），queryKey 才與角色頁的 useBackendItem 共用快取
  const distinct = [];
  const seen = new Set();
  (roleIds || []).forEach((id) => {
    if (id === undefined || id === null || id === "") return;
    const k = String(id);
    if (seen.has(k)) return;
    seen.add(k);
    distinct.push(id);
  });

  const byId = new Map((roles || []).map((r) => [String(r.id), r]));
  const hasModules = (id) => {
    const r = byId.get(String(id));
    return !!(r && Array.isArray(r.modules) && r.modules.length > 0);
  };
  const needDetail = distinct.filter((id) => !hasModules(id));

  const detailQs = useQueries({
    queries: needDetail.map((id) => ({
      queryKey: ["customerRoles", "item", id],
      queryFn: () => customerRolesApi.getById(id),
      staleTime: ROLE_DETAIL_STALE_TIME,
    })),
  });

  const modulesById = new Map();
  distinct.forEach((id) => {
    if (hasModules(id)) modulesById.set(String(id), byId.get(String(id)).modules);
  });
  needDetail.forEach((id, i) => {
    const dto = detailQs[i] && detailQs[i].data;
    if (dto && dto.modules) modulesById.set(String(id), dto.modules);
  });

  return {
    canApprove: (roleId) => roleCanApprove(modulesById.get(String(roleId))),
    isLoading: detailQs.some((q) => q.isLoading),
    error: (detailQs.find((q) => q.error) || {}).error || null,
  };
}

export default useApproverRoles;
